import React from 'react';
import { QueryClient, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { create, windowScheduler } from '@yornaath/batshit';
import { identity, uniq } from 'lodash';

import { stableQueryKey } from '@neptune/shared/common-util';
import { isEmptyShortIdsMap, ShortIdsByProject } from '@neptune/shared/entity-domain';
import { QueryAttributesResult, queryFieldsFromAllPages } from '@neptune/shared/leaderboard-domain';

export type QueryEntry = {
  fields: string[];
  shortIdsByProject: ShortIdsByProject;
};

export type BatchedResult = Awaited<ReturnType<typeof queryFieldsFromAllPages>>;

const EMPTY_ARRAY: never[] = [];

const QUERY_KEY_PREFIX = 'queryAllAttributesForWidget';

export function mergeShortIds(maps: ShortIdsByProject[]): ShortIdsByProject {
  if (maps.length === 0) {
    return new Map();
  }

  const merged = new Map<string, Set<string>>();

  for (const map of maps) {
    for (const [key, values] of map) {
      let set = merged.get(key);

      if (!set) {
        set = new Set();
        merged.set(key, set);
      }

      values.forEach((value) => set.add(value));
    }
  }

  const result = new Map<string, string[]>();
  merged.forEach((valueSet, key) => {
    result.set(key, Array.from(valueSet));
  });

  return result;
}

export function queryFieldsResolver(
  batchedResult: BatchedResult,
  { fields, shortIdsByProject }: QueryEntry,
) {
  return (
    batchedResult
      // match requested Ids first
      .filter(({ shortId, projectIdentifier }) => {
        return shortIdsByProject.get(projectIdentifier)?.includes(shortId);
      })
      // get only requested fields
      .map((entityData) => ({
        ...entityData,
        attributes: entityData.attributes.filter(({ attributeName }) =>
          fields.includes(attributeName),
        ),
      }))
      // comply with semantics of the query as it doesn't return result for missing fields in the entity
      .filter(({ attributes }) => attributes.length > 0)
  );
}

function createQueryAllAttributesForWidgetBatcher({
  includeLastForPreviewSeries,
}: {
  includeLastForPreviewSeries: boolean;
}) {
  return create({
    name: includeLastForPreviewSeries
      ? 'batshit:queryAllAttributesForWidget:includeLastForPreviewSeries'
      : 'batshit:queryAllAttributesForWidget',
    fetcher: (entries: QueryEntry[]) => {
      const fields = uniq(entries.map(({ fields }) => fields).flat());
      const shortIdsByProject = mergeShortIds(
        entries.map(({ shortIdsByProject }) => shortIdsByProject),
      );

      return queryFieldsFromAllPages({
        fields,
        shortIdsByProject,
        includeLastForPreviewSeries,
      });
    },
    resolver: queryFieldsResolver,
    scheduler: windowScheduler(200),
  });
}

const batshitByIncludeLastForPreviewSeries = {
  default: createQueryAllAttributesForWidgetBatcher({ includeLastForPreviewSeries: false }),
  /**
   * temporary workaround to include last for preview series, remove once new ingestion model is deployed
   * includeLastForPreviewSeries is expensive to compute, so we only apply it when really needed()
   */
  includeLastForPreviewSeries: createQueryAllAttributesForWidgetBatcher({
    includeLastForPreviewSeries: true,
  }),
};

export function getQueryAllAttributesForWidgetQueryKey({
  fields,
  includeLastForPreviewSeries,
  shortIdsByProject,
}: {
  fields: string[];
  includeLastForPreviewSeries: boolean;
  shortIdsByProject: ShortIdsByProject;
}) {
  return stableQueryKey(QUERY_KEY_PREFIX, shortIdsByProject, fields, {
    includeLastForPreviewSeries,
  });
}

export const useQueryAllAttributesForWidget = ({
  fields = EMPTY_ARRAY,
  performanceStrategy = 'default',
  shortIdsByProject = new Map(),
  enabled: enabledProp = true,
  includeLastForPreviewSeries = false,
}: {
  fields?: string[];
  performanceStrategy?: 'long-lasting-data' | 'default';
  shortIdsByProject?: ShortIdsByProject;
  enabled?: boolean;
  includeLastForPreviewSeries?: boolean;
}): UseQueryResult<QueryAttributesResult[]> => {
  const enabled = !!fields.length && !isEmptyShortIdsMap(shortIdsByProject) && enabledProp;

  return useQuery({
    enabled,
    queryKey: getQueryAllAttributesForWidgetQueryKey({
      fields,
      includeLastForPreviewSeries,
      shortIdsByProject,
    }),
    queryFn: async () => {
      if (!fields.length || isEmptyShortIdsMap(shortIdsByProject)) {
        return EMPTY_ARRAY;
      }

      const batshit = includeLastForPreviewSeries
        ? batshitByIncludeLastForPreviewSeries.includeLastForPreviewSeries
        : batshitByIncludeLastForPreviewSeries.default;

      return batshit.fetch({
        fields,
        shortIdsByProject,
      });
    },
    staleTime: performanceStrategy === 'long-lasting-data' ? 60_000 : 2_000,
    keepPreviousData: enabled,
  });
};

const extractData = (queryClient: QueryClient) =>
  queryClient
    .getQueriesData({ queryKey: [QUERY_KEY_PREFIX], exact: false })
    .filter(([, state]) => state !== undefined)
    .map(([, state]) => state as QueryAttributesResult[]);

export function useGetAllAttributesForWidget(
  selector: (data: Array<QueryAttributesResult[]>) => Set<string> = identity,
): Set<string> {
  const queryClient = useQueryClient();

  return React.useMemo(
    () => selector(extractData(queryClient)),

    [queryClient, selector],
  );
}

const EVENTS_WHITELIST = new Set(['updated', 'removed', 'added']);

export function useSubscribeAllAttributesForWidget(
  callback: (data: Array<QueryAttributesResult[]>) => void,
) {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    let hasPendingFlush = false;
    let animationFrameRequestId: number | undefined;
    const queryCache = queryClient.getQueryCache();

    const flush = () => {
      animationFrameRequestId = undefined;

      if (!hasPendingFlush) {
        return;
      }

      hasPendingFlush = false;
      callback(extractData(queryClient));
    };

    const scheduleFlush = () => {
      hasPendingFlush = true;

      if (animationFrameRequestId !== undefined) {
        return;
      }

      animationFrameRequestId = window.requestAnimationFrame(flush);
    };

    const unsubscribe = queryCache.subscribe((event) => {
      if (event.query.queryKey?.[0] !== QUERY_KEY_PREFIX || !EVENTS_WHITELIST.has(event.type)) {
        return;
      }

      scheduleFlush();
    });

    callback(extractData(queryClient));

    return () => {
      unsubscribe();

      if (animationFrameRequestId !== undefined) {
        window.cancelAnimationFrame(animationFrameRequestId);
      }
    };
  }, [queryClient, callback]);
}
